/**
 * Dashboard API service
 * Calls real API when available, falls back to mock data in dev.
 * Swap mock implementations for `api.get(...)` when backend is live.
 */
import api from '@/lib/api';
import { MOCK_WORK_ORDERS } from '@/data/mockData';
import { MOCK_ASSETS } from '@/data/mockAssets';
import type { WorkOrder } from '@/types/models';

// ── Types ──────────────────────────────────────────────

export interface DashboardKpis {
  received_today: number;
  processed_today: number;
  pending_wipes: number;
  open_exceptions: number;
  revenue_recovered: number;
  pending_shipments: number;
}

export interface ThroughputPoint {
  date: string;
  received: number;
  processed: number;
}

export interface CategoryBreakdown {
  name: string;
  value: number;
}

export interface DashboardData {
  kpis: DashboardKpis;
  throughput: ThroughputPoint[];
  category_breakdown: CategoryBreakdown[];
  upcoming_work_orders: WorkOrder[];
  recent_work_orders: WorkOrder[];
}

// ── Mock helpers ───────────────────────────────────────

function buildMockKpis(): DashboardKpis {
  const today = new Date().toISOString().split('T')[0];
  const receivedToday = MOCK_ASSETS.filter(
    (a) => a.created_at.startsWith(today)
  ).length || MOCK_ASSETS.length; // fallback to total if none today
  const processedToday = MOCK_ASSETS.filter(
    (a) => ['sanitized', 'sold', 'recycled', 'completed'].includes(a.status) && a.updated_at.startsWith(today)
  ).length;

  return {
    received_today: receivedToday,
    processed_today: processedToday,
    pending_wipes: MOCK_ASSETS.filter((a) => a.status === 'sanitization_pending').length,
    open_exceptions: MOCK_WORK_ORDERS.filter((wo) => wo.status === 'cancelled').length,
    revenue_recovered: 8420,
    pending_shipments: MOCK_WORK_ORDERS.filter((wo) => wo.status === 'scheduled').length,
  };
}

function buildMockThroughput(): ThroughputPoint[] {
  // Generate last 7 days of simulated data seeded from work order counts
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const baseReceived = Math.max(10, MOCK_ASSETS.length * 3);
  return days.slice(0, 6).map((day, i) => ({
    date: day,
    received: Math.round(baseReceived * (0.6 + Math.sin(i) * 0.4)),
    processed: Math.round(baseReceived * (0.5 + Math.cos(i) * 0.35)),
  }));
}

function buildMockCategoryBreakdown(): CategoryBreakdown[] {
  const counts: Record<string, number> = {};
  MOCK_ASSETS.forEach((a) => {
    const cat = a.category_name || 'Other';
    counts[cat] = (counts[cat] || 0) + (a.quantity || 1);
  });

  return Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

function buildMockUpcoming(): WorkOrder[] {
  const today = new Date().toISOString().split('T')[0];
  return MOCK_WORK_ORDERS.filter(
    (wo) => wo.scheduled_date >= today && (wo.status === 'scheduled' || wo.status === 'in_progress')
  ).sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
}

function buildMockRecent(): WorkOrder[] {
  return [...MOCK_WORK_ORDERS]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, 5);
}

// ── Service ────────────────────────────────────────────

const USE_MOCK = !import.meta.env.VITE_API_URL || import.meta.env.VITE_USE_MOCK === 'true';

export async function fetchDashboardData(): Promise<DashboardData> {
  if (USE_MOCK) {
    // Simulate network delay
    await new Promise((r) => setTimeout(r, 400));
    return {
      kpis: buildMockKpis(),
      throughput: buildMockThroughput(),
      category_breakdown: buildMockCategoryBreakdown(),
      upcoming_work_orders: buildMockUpcoming(),
      recent_work_orders: buildMockRecent(),
    };
  }

  const res = await api.get('/api/dashboard');
  const raw = res.data as Record<string, unknown>;
  return {
    kpis: (raw.kpis ?? {}) as DashboardKpis,
    throughput: Array.isArray(raw.throughput) ? raw.throughput : [],
    category_breakdown: Array.isArray(raw.category_breakdown) ? raw.category_breakdown : [],
    upcoming_work_orders: Array.isArray(raw.upcoming_work_orders) ? raw.upcoming_work_orders : [],
    recent_work_orders: Array.isArray(raw.recent_work_orders) ? raw.recent_work_orders : [],
  } as DashboardData;
}

export async function fetchDashboardKpis(): Promise<DashboardKpis> {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 300));
    return buildMockKpis();
  }
  const res = await api.get('/api/dashboard/kpis');
  return (res.data?.data ?? res.data) as DashboardKpis;
}

export async function fetchThroughput(): Promise<ThroughputPoint[]> {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 350));
    return buildMockThroughput();
  }
  const res = await api.get('/api/dashboard/throughput');
  const raw = res.data;
  return Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
}

export async function fetchCategoryBreakdown(): Promise<CategoryBreakdown[]> {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 300));
    return buildMockCategoryBreakdown();
  }
  const res = await api.get('/api/dashboard/categories');
  const raw = res.data;
  return Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
}
